import { ITransaction } from '@/types'
import type { Order } from '../UI/DataTable'
import { aliasLabel } from './aliasOptions'

export type TransactionSortKey = keyof ITransaction

// Newest first, matching what the read is meant to return. Neither backing store actually sorts
// by transaction date, so the order on screen is the one applied here. Any other order is one the
// user asked for, and is answered from the customer's whole history rather than the rows in hand.
export const DEFAULT_SORT_BY: TransactionSortKey = 'transactionDate'
export const DEFAULT_SORT_ORDER: Order = 'desc'

export const isDefaultSort = (sortBy: TransactionSortKey, order: Order) =>
  sortBy === DEFAULT_SORT_BY && order === DEFAULT_SORT_ORDER

// What a row is compared on, or undefined when it has nothing usable to compare.
const sortValue = (transaction: ITransaction, sortBy: TransactionSortKey) => {
  const value = transaction[sortBy]

  if (sortBy === 'transactionDate' || sortBy === 'createdAt') {
    const time = new Date(String(value)).getTime()
    return Number.isNaN(time) ? undefined : time
  }
  if (sortBy === 'amount') {
    const amount = typeof value === 'number' ? value : Number(value)
    return value === '' || value == null || Number.isNaN(amount) ? undefined : amount
  }

  // Ordered by what the column shows, so an alias sorts by its label rather than its code.
  const text = sortBy === 'descriptionAlias' ? aliasLabel(String(value ?? '')) : value
  return typeof text === 'string' && text.trim() !== '' ? text.trim() : undefined
}

const collator = new Intl.Collator('en-AU', { numeric: true, sensitivity: 'base' })

export const sortTransactions = (
  rows: ITransaction[],
  sortBy: TransactionSortKey,
  order: Order
): ITransaction[] => {
  const direction = order === 'asc' ? 1 : -1

  return rows
    .map(row => ({ row, value: sortValue(row, sortBy) }))
    .sort((left, right) => {
      // A row with nothing to compare sinks to the bottom rather than scrambling the ones around it.
      if (left.value === undefined) return right.value === undefined ? 0 : 1
      if (right.value === undefined) return -1

      const compared =
        typeof left.value === 'number' && typeof right.value === 'number'
          ? left.value - right.value
          : collator.compare(String(left.value), String(right.value))
      return compared * direction
    })
    .map(({ row }) => row)
}
